import { useEffect, useState } from 'react';
import { artworkService } from '../services/artworkService';
import { registerObjectUrl, revokeObjectUrl } from '../utils/objectUrlRegistry';

export type ArtworkImageVariant = 'image' | 'thumbnail';
export type ArtworkImageAccess = 'public' | 'admin';

export interface ArtworkObjectUrlState {
  url: string | null;
  isLoading: boolean;
  error: unknown | null;
}

export function useArtworkObjectUrlState(
  artworkId: number | null | undefined,
  variant: ArtworkImageVariant,
  updatedAt?: string,
  access: ArtworkImageAccess = 'public',
  enabled = true,
  reloadKey = 0,
): ArtworkObjectUrlState {
  const requestKey = `${access}:${artworkId ?? 'none'}:${variant}:${updatedAt ?? 'none'}:${reloadKey}`;
  const [loadedObject, setLoadedObject] = useState<{ key: string; url: string } | null>(null);
  const [loadFailure, setLoadFailure] = useState<{ key: string; error: unknown } | null>(null);
  const [blockedRequestKey, setBlockedRequestKey] = useState<string | null>(null);
  const hasSource = enabled && artworkId !== null && artworkId !== undefined;

  useEffect(() => {
    let disposed = false;
    let loadedUrl: string | null = null;
    const controller = new AbortController();

    setLoadedObject(null);
    setLoadFailure(null);
    setBlockedRequestKey(null);

    const handleGlobalRevocation = () => {
      if (!disposed) {
        disposed = true;
        controller.abort();
        loadedUrl = null;
        setLoadedObject(null);
        setBlockedRequestKey(requestKey);
      }
    };
    window.addEventListener('artwork-object-urls-revoked', handleGlobalRevocation);

    if (!enabled || artworkId === null || artworkId === undefined) {
      return () => {
        window.removeEventListener('artwork-object-urls-revoked', handleGlobalRevocation);
        controller.abort();
      };
    }

    const load = async () => {
      try {
        const blob = access === 'admin'
          ? await artworkService.getAdminArtworkBlob(artworkId, variant, controller.signal)
          : await artworkService.getPublicArtworkBlob(artworkId, variant, updatedAt, controller.signal);
        if (disposed) return;

        loadedUrl = URL.createObjectURL(blob);
        registerObjectUrl(loadedUrl);
        setLoadedObject({ key: requestKey, url: loadedUrl });
      } catch {
        if (!disposed) {
          setLoadedObject(null);
          setLoadFailure({ key: requestKey, error: new Error('Artwork image could not be loaded') });
        }
      }
    };

    void load();

    return () => {
      disposed = true;
      controller.abort();
      window.removeEventListener('artwork-object-urls-revoked', handleGlobalRevocation);
      revokeObjectUrl(loadedUrl);
      loadedUrl = null;
    };
  }, [access, artworkId, enabled, requestKey, updatedAt, variant]);

  const currentUrl = hasSource && loadedObject?.key === requestKey ? loadedObject.url : null;
  const currentError = hasSource && loadFailure?.key === requestKey ? loadFailure.error : null;
  return {
    url: currentUrl,
    isLoading: hasSource && blockedRequestKey !== requestKey && currentUrl === null && currentError === null,
    error: currentError,
  };
}

export function useArtworkObjectUrl(
  artworkId: number | null | undefined,
  variant: ArtworkImageVariant,
  updatedAt?: string,
  access: ArtworkImageAccess = 'public',
  enabled = true,
  reloadKey = 0,
): string | null {
  return useArtworkObjectUrlState(artworkId, variant, updatedAt, access, enabled, reloadKey).url;
}
